import React from 'react';
import UnifiedPdfCitationViewer from './UnifiedPdfCitationViewer';
import PdfViewer from './PdfViewer';

interface PdfDocumentViewerProps {
  documentId: string;
  citation?: {
    documentId: string;
    documentName: string;
    page: number;
    startOffset: number;
    endOffset: number;
    text: string;
    context?: string;
  };
  onClose: () => void;
}

/**
 * Legacy PDF Document Viewer - Redirects to UnifiedPdfCitationViewer
 * 
 * This component has been replaced with UnifiedPdfCitationViewer
 * to avoid PDF.js version conflicts.
 */
const PdfDocumentViewer: React.FC<PdfDocumentViewerProps> = ({
  documentId,
  citation,
  onClose
}) => {
  // Always call hooks at the top level
  const [documentUrl, setDocumentUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(!citation); // Only load if no citation provided
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!citation) {
      // If no citation provided, use the streaming endpoint
      const fetchDocumentUrl = async () => {
        try {
          console.log('📄 PdfDocumentViewer: Using stream endpoint for document:', documentId);
          const streamUrl = `${process.env.REACT_APP_API_URL}/api/documents/${documentId}/stream`;
          console.log('📄 PdfDocumentViewer: Stream URL:', streamUrl);
          setDocumentUrl(streamUrl);
        } catch (err) {
          console.error('📄 PdfDocumentViewer: Error setting stream URL:', err);
          setError('Failed to load document');
        } finally {
          setLoading(false);
        }
      };

      fetchDocumentUrl();
    }
  }, [documentId, citation]);

  if (!citation) {
    // Handle the no-citation case
    if (loading) {
      return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Loading Document...</h2>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        </div>
      );
    }

    if (error || !documentUrl) {
      return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-4 text-red-600">Error Loading Document</h2>
            <p className="mb-4">{error || 'Document URL not available'}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    return (
      <PdfViewer
        fileUrl={documentUrl}
        onClose={onClose}
      />
    );
  }

  return (
    <UnifiedPdfCitationViewer
      documentId={documentId}
      citation={citation}
      onClose={onClose}
    />
  );
};

export default PdfDocumentViewer;